import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChevronLeft, Bed, Bath, Compass, Maximize, ArrowUpRight, ImageOff, Loader2, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  available: { labelEs: 'Disponible', labelEn: 'Available', color: 'bg-green-100 text-green-800 border-green-200' },
  reserved: { labelEs: 'Reservado', labelEn: 'Reserved', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  sold: { labelEs: 'Vendido', labelEn: 'Sold', color: 'bg-red-100 text-red-800 border-red-200' },
};

export default function FloorplanView() {
  const { floor, towerId } = useParams();
  const floorNum = parseInt(floor);
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [selectedApt, setSelectedApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [floorPlan, setFloorPlan] = useState(null);
  const [viewingAptPlan, setViewingAptPlan] = useState(false); // true = viewing individual apt plan

  useEffect(() => {
    const towerParam = towerId ? `?tower=${towerId}` : '';
    Promise.all([
      axios.get(`${API}/apartments/floor/${floorNum}${towerParam}`),
      towerId ? axios.get(`${API}/floor-plans/${towerId}/${floorNum}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]).then(([aptsRes, planRes]) => {
      setApartments(aptsRes.data);
      if (aptsRes.data.length > 0) setSelectedApt(aptsRes.data[0]);
      setFloorPlan(planRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [floorNum, towerId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  // Check if an image path is from object storage (not a default placeholder)
  const isStorageImage = (path) => path && !path.startsWith('/floorplan/');

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const towerName = towerId === 'kirei1' ? 'Kirei I' : towerId === 'kirei2' ? 'Kirei II' : '';
  const hasAptFloorplan = selectedApt && isStorageImage(selectedApt.floorplan_image);
  const hasFloorPlan = floorPlan?.floorplan_image;
  const galleryImages = selectedApt?.gallery_images?.filter(isStorageImage) || [];

  return (
    <div data-testid="floorplan-view" className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <button
            onClick={() => {
              const backPath = towerId ? `/tower/${towerId}` : '/';
              navigate(backPath);
              setTimeout(() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' }), 300);
            }}
            data-testid="back-to-building"
            className="inline-flex items-center gap-2 text-[#8A828F] hover:text-[#D4AF37] transition-colors mb-6 text-sm tracking-[0.1em] uppercase"
          >
            <ChevronLeft size={16} />
            {t('floorplan.backToBuilding')}
          </button>
          <div className="flex items-end gap-4">
            <h1
              className="text-4xl sm:text-5xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
              data-testid="floorplan-title"
            >
              {towerName && <span className="text-[#8A828F] text-2xl mr-3">{towerName}</span>}
              {t('building.floor')} {floorNum}
            </h1>
            <span className="text-[#8A828F] text-sm mb-2 tracking-[0.1em] uppercase">
              {apartments.length} {apartments.length > 1 ? t('building.units') : t('building.unit')}
            </span>
          </div>
        </motion.div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Left: Floorplan + Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-7"
          >
            {/* Floorplan image — floor-level plan takes priority, then per-apartment plan */}
            <div className="relative bg-[#F5F5F7] border border-[#E5E5E5] overflow-hidden group">
              {viewingAptPlan && selectedApt && isStorageImage(selectedApt.floorplan_image) ? (
                <StorageImage
                  path={selectedApt.floorplan_image}
                  alt={`${selectedApt.id} individual plan`}
                  className="w-full h-auto min-h-[300px] object-contain transition-transform duration-700 group-hover:scale-105"
                  testid="apt-floorplan-image"
                />
              ) : hasFloorPlan ? (
                <StorageImage
                  path={floorPlan.floorplan_image}
                  alt={`Floor ${floorNum} plan`}
                  className="w-full h-auto min-h-[300px] object-contain transition-transform duration-700 group-hover:scale-105"
                  testid="floorplan-image"
                />
              ) : hasAptFloorplan ? (
                <StorageImage
                  path={selectedApt.floorplan_image}
                  alt={`${selectedApt.id} floor plan`}
                  className="w-full h-auto min-h-[300px] object-contain transition-transform duration-700 group-hover:scale-105"
                  testid="floorplan-image"
                />
              ) : (
                <FloorplanPlaceholder apartments={apartments} selectedApt={selectedApt} setSelectedApt={setSelectedApt} t={t} floorNum={floorNum} lang={lang} />
              )}
              {/* Floor label */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 border border-[#E5E5E5]">
                <span className="text-xs tracking-[0.15em] uppercase text-[#8A828F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {viewingAptPlan
                    ? `${lang === 'es' ? 'Plano Individual' : 'Individual Plan'} — ${selectedApt?.id}`
                    : `${t('floorplan.title')} — ${selectedApt?.id || `${t('building.floor')} ${floorNum}`}`}
                </span>
              </div>
              {/* Toggle between floor plan and individual plan */}
              {selectedApt && isStorageImage(selectedApt.floorplan_image) && hasFloorPlan && (
                <button
                  onClick={() => setViewingAptPlan(!viewingAptPlan)}
                  data-testid="toggle-plan-view"
                  className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm border border-[#E5E5E5] px-4 py-2 text-xs tracking-[0.1em] uppercase text-[#8A828F] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                >
                  {viewingAptPlan
                    ? (lang === 'es' ? 'Ver Plano del Nivel' : 'View Floor Plan')
                    : (lang === 'es' ? 'Ver Plano Individual' : 'View Unit Plan')}
                </button>
              )}
            </div>

            {/* Gallery images */}
            {galleryImages.length > 0 ? (
              <div className={`grid gap-4 mt-4 ${galleryImages.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {galleryImages.map((img, idx) => (
                  <div key={idx} className="overflow-hidden border border-[#E5E5E5]">
                    <StorageImage
                      path={img}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-40 object-cover hover:scale-105 transition-transform duration-700"
                      testid={`gallery-image-${idx}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 border border-dashed border-[#E5E5E5] bg-[#F5F5F7] p-8 flex flex-col items-center justify-center text-center">
                <ImageOff size={32} className="text-[#E5E5E5] mb-3" />
                <p className="text-xs text-[#8A828F] tracking-[0.08em]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {lang === 'es'
                    ? 'Fotos de galería disponibles próximamente'
                    : 'Gallery photos coming soon'}
                </p>
                <p className="text-[10px] text-[#C5C0C8] mt-1">
                  {lang === 'es'
                    ? 'El administrador puede subir fotos desde el panel CMS'
                    : 'Admin can upload photos from the CMS panel'}
                </p>
              </div>
            )}
          </motion.div>

          {/* Right: Apartment Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-5"
          >
            {/* Unit selector tabs */}
            <div className="flex flex-wrap gap-3 mb-8">
              {apartments.map(apt => (
                <button
                  key={apt.id}
                  data-testid={`unit-tab-${apt.id}`}
                  onClick={() => { setSelectedApt(apt); setViewingAptPlan(false); }}
                  className={`px-5 py-2.5 text-xs tracking-[0.1em] uppercase transition-all duration-300 border ${
                    selectedApt?.id === apt.id
                      ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                      : 'bg-white text-[#8A828F] border-[#E5E5E5] hover:border-[#D4AF37] hover:text-[#D4AF37]'
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {apt.unit}
                  {isStorageImage(apt.floorplan_image) && (
                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#2E7D32]" title={lang === 'es' ? 'Tiene plano' : 'Has plan'} />
                  )}
                </button>
              ))}
            </div>

            {/* View individual plan button */}
            {selectedApt && isStorageImage(selectedApt.floorplan_image) && !viewingAptPlan && (
              <button
                onClick={() => setViewingAptPlan(true)}
                data-testid={`view-apt-plan-${selectedApt.id}`}
                className="mb-6 flex items-center gap-2 w-full border border-[#D4AF37]/40 text-[#D4AF37] py-3 justify-center text-xs tracking-[0.1em] uppercase hover:bg-[#D4AF37]/5 transition-colors"
              >
                <Maximize size={14} />
                {lang === 'es' ? `Ver Plano de ${selectedApt.unit}` : `View ${selectedApt.unit} Floor Plan`}
              </button>
            )}

            <AnimatePresence mode="wait">
              {selectedApt && (
                <motion.div
                  key={selectedApt.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`apartment-details-${selectedApt.id}`}
                >
                  <div className="border border-[#E5E5E5] bg-white">
                    <div className="p-8 md:p-10 border-b border-[#E5E5E5]">
                      <div className="flex items-center justify-between mb-6">
                        <h2
                          className="text-3xl font-light tracking-tight"
                          style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
                        >
                          {selectedApt.id}
                        </h2>
                        <Badge
                          data-testid={`status-badge-${selectedApt.id}`}
                          className={`${statusConfig[selectedApt.status]?.color || ''} border text-xs tracking-[0.1em] uppercase px-3 py-1`}
                        >
                          {lang === 'es'
                            ? statusConfig[selectedApt.status]?.labelEs
                            : statusConfig[selectedApt.status]?.labelEn}
                        </Badge>
                      </div>

                      <div
                        className="text-3xl font-light mb-2 gold-text"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        data-testid={`price-${selectedApt.id}`}
                      >
                        {formatPrice(selectedApt.price)}
                      </div>
                      <p className="text-sm text-[#8A828F]">{selectedApt.area_m2} m²</p>
                    </div>

                    {/* Description (if set via CMS) */}
                    {selectedApt.description && (
                      <div className="px-8 md:px-10 pt-6 pb-2">
                        <p className="text-sm text-[#555] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`description-${selectedApt.id}`}>
                          {selectedApt.description}
                        </p>
                      </div>
                    )}

                    {/* Details grid */}
                    <div className="p-8 md:p-10 grid grid-cols-2 gap-6">
                      <DetailItem icon={<Maximize size={18} />} label={t('floorplan.area')} value={`${selectedApt.area_m2} m²`} />
                      <DetailItem icon={<Bed size={18} />} label={t('floorplan.bedrooms')} value={selectedApt.bedrooms} />
                      <DetailItem icon={<Bath size={18} />} label={t('floorplan.bathrooms')} value={selectedApt.bathrooms} />
                      <DetailItem icon={<Compass size={18} />} label={t('floorplan.orientation')} value={selectedApt.orientation} />
                      <div className="col-span-2">
                        <DetailItem
                          icon={<ArrowUpRight size={18} />}
                          label={t('floorplan.balcony')}
                          value={selectedApt.has_balcony ? t('floorplan.yes') : t('floorplan.no')}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    {selectedApt.status === 'available' && (
                      <div className="p-8 md:p-10 pt-0 flex gap-4">
                        <button
                          data-testid={`reserve-btn-${selectedApt.id}`}
                          className="flex-1 bg-[#D4AF37] text-white py-4 uppercase tracking-[0.1em] text-sm font-light hover:bg-[#B5952F] transition-colors duration-300"
                        >
                          {t('floorplan.reserve')}
                        </button>
                        <button
                          data-testid={`contact-btn-${selectedApt.id}`}
                          className="flex-1 border-2 border-[#D4AF37] text-[#D4AF37] py-4 uppercase tracking-[0.1em] text-sm font-light hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
                        >
                          {t('floorplan.contact')}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* Placeholder shown when no floorplan image has been uploaded */
function FloorplanPlaceholder({ apartments, selectedApt, setSelectedApt, t, floorNum, lang }) {
  return (
    <div className="w-full min-h-[350px] bg-[#F5F5F7] flex flex-col items-center justify-center p-8" data-testid="floorplan-placeholder">
      {/* Interactive apartment buttons as visual placeholder */}
      <p className="text-xs tracking-[0.15em] uppercase text-[#8A828F] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {t('building.floor')} {floorNum} — {lang === 'es' ? 'Distribución' : 'Layout'}
      </p>
      <div className="flex flex-wrap gap-4 justify-center mb-8">
        {apartments.map((apt) => (
          <motion.button
            key={apt.id}
            data-testid={`floorplan-marker-${apt.id}`}
            onClick={() => setSelectedApt(apt)}
            className={`w-16 h-16 flex items-center justify-center border-2 transition-all duration-300 ${
              selectedApt?.id === apt.id
                ? 'bg-[#D4AF37] border-[#D4AF37] text-white scale-110'
                : 'bg-white/80 border-[#8A828F] text-[#8A828F] hover:border-[#D4AF37] hover:text-[#D4AF37]'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-xs font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {apt.unit}
            </span>
          </motion.button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[#C5C0C8]">
        <Upload size={14} />
        <span className="text-[10px] tracking-[0.08em]">
          {lang === 'es'
            ? 'Plano disponible próximamente — editable desde CMS'
            : 'Floor plan coming soon — editable from CMS'}
        </span>
      </div>
    </div>
  );
}

/* Fetches and displays image from Object Storage */
function StorageImage({ path, alt, className, testid }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path) return;
    let revoke = null;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        setSrc(url);
        revoke = url;
      })
      .catch(() => setError(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);

  if (error) {
    return (
      <div className={`bg-[#F5F5F7] flex items-center justify-center ${className}`}>
        <ImageOff size={24} className="text-[#E5E5E5]" />
      </div>
    );
  }

  if (!src) {
    return (
      <div className={`bg-[#F5F5F7] flex items-center justify-center ${className}`}>
        <Loader2 size={20} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} data-testid={testid} />;
}

function DetailItem({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[#D4AF37] mt-0.5">{icon}</div>
      <div>
        <p className="text-xs text-[#8A828F] tracking-[0.1em] uppercase mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {label}
        </p>
        <p className="text-sm font-medium text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {value}
        </p>
      </div>
    </div>
  );
}
